import React, {useState, useEffect} from "react";
import {
    Box,
    Text,
    Textarea,
    Button,
    useColorModeValue,
    useToast,
    Flex,
    Spinner,
    Stack
} from "@chakra-ui/react";
import {cqAPI} from "../../lib/Auth";

const MetadataPrompts = () => {
    const bgColor = useColorModeValue("gray.100", "gray.700");
    const cardBg = useColorModeValue("#ffffff", "#212330");
    const borderClr = useColorModeValue("gray.200", "gray.600");

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const defaultFormData = {
        summarizer_prompt: "",
        call_summarizer_prompt: "",
        patient_conv_data_extractor_setup_prompts: {base_prompt: "", type_call_prompt: "", output_format_prompt: ""}
    };


    const [formData, setFormData] = useState(defaultFormData);
    const toast = useToast();

    // ----------- LOAD PROMPTS -----------
    useEffect(() => {
        const fetchMetadataPrompts = async () => {
            // setLoading(true);
            try {
                const response = await cqAPI.get("/prompts/metadata");
                if (response.type === "ok" && response.data) {
                    setFormData(response.data);
                } else {
                    console.warn("Invalid or missing response data, using defaults");
                    setFormData(defaultFormData);
                    toast({
                        title: "Warning",
                        description: "Loaded default Metadata Prompts because the API response was invalid.",
                        status: "warning",
                        duration: 3000,
                        isClosable: true,
                    });
                }
            } catch (error) {
                console.error("Error loading PO Prompt:", error);
                toast({
                    title: "Error",
                    description: "Failed to load PO Prompt.",
                    status: "error",
                    duration: 3000,
                    isClosable: true
                });
            } finally {
                setLoading(false);
            }
        };
        fetchMetadataPrompts();
    }, []);

    // ----------- SAVE PROMPTS -----------
    const handleSave = async () => {
        setSaving(true);
        try {
            const response = await cqAPI.put("/prompts/metadata", formData);
            if (response.type === "ok") {
                toast({
                    title: "Saved",
                    description: "Metadata Prompts updated successfully.",
                    status: "success",
                    duration: 3000,
                    isClosable: true
                });
            } else {
                toast({
                    title: "Error",
                    description: response.msg || 'Failed to save!',
                    status: "error",
                    duration: 3000,
                    isClosable: true,
                });
            }
        } catch (error) {
            console.error("Error saving Metadata Prompt:", error);
            toast({
                title: "Error",
                description: "Failed to save Metadata Prompt.",
                status: "error",
                duration: 3000,
                isClosable: true
            });
        } finally {
            setSaving(false);
        }
    };

    // ----------- RENDER UI -----------
    return (
        <Box bg={bgColor} minH="100dvh" w="100%">
            <Box bg={bgColor} p={6} borderRadius="md" w="100%" maxW="100%">
            <Flex justify="space-between" align="center" mb={6}>
                <Text fontSize="xl" fontWeight="bold">
                    Metadata Prompts
                </Text>
                <Button
                    colorScheme="blue"
                    onClick={handleSave}
                    isLoading={saving}
                    loadingText="Saving"
                >
                    Save
                </Button>
            </Flex>

            {loading ? (
                <Flex justify="center" align="center" h="200px">
                    <Spinner size="xl"/>
                </Flex>
            ) : (
                <Stack spacing={6}>
                    {/* Summarizer Prompt */}
                    <Box bg={cardBg} p={4} borderRadius="md" border="1px solid" borderColor={borderClr}>
                        <Text fontWeight="semibold" mb={2}>
                            Summarizer Prompt
                        </Text>
                        <Textarea
                            minH="120px"
                            resize="vertical"
                            value={formData.summarizer_prompt}
                            onChange={(e) =>
                                setFormData({...formData, summarizer_prompt: e.target.value})
                            }
                            placeholder="Summarizer Prompt..."
                        />
                    </Box>

                    {/* Call Summarizer Prompt */}
                    <Box bg={cardBg} p={4} borderRadius="md" border="1px solid" borderColor={borderClr}>
                        <Text fontWeight="semibold" mb={2}>
                            Call Summarizer Prompt
                        </Text>
                        <Textarea
                            minH="120px"
                            resize="vertical"
                            value={formData.call_summarizer_prompt}
                            onChange={(e) =>
                                setFormData({...formData, call_summarizer_prompt: e.target.value})
                            }
                            placeholder="Call Summarizer Prompt..."
                        />
                    </Box>

                    {/* Patient Conv Data Extractor Setup Prompts */}
                    <Box bg={cardBg} p={4} borderRadius="md" border="1px solid" borderColor={borderClr}>
                        <Text fontWeight="semibold" mb={2}>
                            Patient Conv Data Extractor Setup Prompts
                        </Text>
                        <Stack spacing={3}>
                          <Text alignSelf="start">Base Prompt </Text>
                          <Textarea
                                minH="100px"
                                resize="vertical"
                                value={formData.patient_conv_data_extractor_setup_prompts.base_prompt}
                                onChange={(e) =>
                                    setFormData({
                                        ...formData,
                                        patient_conv_data_extractor_setup_prompts: {
                                            ...formData.patient_conv_data_extractor_setup_prompts,
                                            base_prompt: e.target.value,
                                        },
                                    })
                                }
                                placeholder="Base Prompt..."
                            />
                          <Text alignSelf="start">Type Call Prompt: </Text>
                           <Textarea
                                minH="100px"
                                resize="vertical"
                                value={formData.patient_conv_data_extractor_setup_prompts.type_call_prompt}
                                onChange={(e) =>
                                    setFormData({
                                        ...formData,
                                        patient_conv_data_extractor_setup_prompts: {
                                            ...formData.patient_conv_data_extractor_setup_prompts,
                                            type_call_prompt: e.target.value,
                                        },
                                    })
                                }
                                placeholder="Type Call Prompt..."
                            />
                          <Text alignSelf="start">Output Format Prompt: </Text>
                          <Textarea
                                minH="100px"
                                resize="vertical"
                                value={formData.patient_conv_data_extractor_setup_prompts.output_format_prompt}
                                onChange={(e) =>
                                    setFormData({
                                        ...formData,
                                        patient_conv_data_extractor_setup_prompts: {
                                            ...formData.patient_conv_data_extractor_setup_prompts,
                                            output_format_prompt: e.target.value,
                                        },
                                    })
                                }
                                placeholder="Output Prompt..."
                            />
                        </Stack>
                    </Box>

                </Stack>
            )}
        </Box>
        </Box>

    );
};

export default MetadataPrompts;